import { Box, Divider, Stack, Typography } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import TuneIcon from "@mui/icons-material/Tune";
import GroupsIcon from "@mui/icons-material/Groups";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import SupportIcon from "@mui/icons-material/Support";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LockIcon from "@mui/icons-material/Lock";
import parse from "html-react-parser";
import StarIcon from "@mui/icons-material/Star";
import Button from "../../Button";
import { planMap } from "../constant";
import { mixpanelEvent } from "../../../Utils/mixpanelUtils";
import { useGlobalContext } from "../../../Context/GlobalContext";
import { useNavigate } from "react-router-dom";
import { lowerFirst } from "lodash";

export const PricingPlan = (props) => {
  const { plan, currentTab, stripePortalSessionUrl } = props;

  const {
    plan: { eai },
    isPayingUser,
  } = useGlobalContext();

  const navigate = useNavigate();

  const userPlan = planMap?.[eai?.plan];
  const isTrial = eai?.plan === "Trial";
  const isPromoted = plan?.["Promoted"];
  const isFree = plan?.["Plan"] === "Free";
  const features = plan?.["Features"];
  const currentPlan = plan?.["Plan"];
  const priceMap = {
    monthly: plan?.["Monthly Price"],
    quarterly: plan?.["Quarterly Price"],
    annually: plan?.["Annual Price"],
  };

  const iconColor = isFree ? "#A0A8AE" : "var(--color-primary)";

  const expiredFeatures = [
    "Unlimited comments",
    "Advanced AI models",
    "Custom tones",
    "Customer Feed monitoring",
  ];

  const formatFeatureText = (rawText) => {
    let text = rawText;

    text = text
      .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, " ")
      .replace(/[‐‑‒–—−]/g, "-")
      .replace(/&nbsp;|&#160;/gi, " ");

    if (currentPlan === "Starter") {
      text = text.replace(
        /Advanced AI model\s*\(\s*GPT-4o\s*\)/i,
        "Advanced AI model (OpenAI)"
      );
    }

    if (currentPlan === "Pro") {
      text = text.replace(
        /Advanced AI models\s*\(\s*GPT-4o\s*,\s*GPT-4\s*\)/i,
        "Advanced AI models (OpenAI, Gemini)"
      );
    }

    if (currentPlan === "Elite") {
      text = text.replace(
        /Advanced AI models\s*\(\s*Google Gemini\s*,\s*GPT-4o\s*,\s*GPT-4\s*\)/i,
        "Advanced AI models (OpenAI, Gemini, Claude)"
      );
    }

    text = text
      .replace(/Weekly\s+monitoring\s+update/gi, "Weekly feed update")
      .replace(
        /Daily\s+and\s+real-time(?=\s*(?:<\/(?:b|strong)>)?\s*monitoring)/gi,
        "Twice a day and real-time"
      )
      .replace(
        /Daily\s+and\s+realtime(?=\s*(?:<\/(?:b|strong)>)?\s*monitoring)/gi,
        "Twice a day and real-time"
      )
      .replace(/\bmonitoring\s+update\b/gi, "feed update")
      .replace(
        /(\d[\d,]*)\s+prospects?\s+monitoring\b/gi,
        "$1 Customers Feed"
      )
      .replace(/(\d[\d,]*)\s+prospects?\b/gi, "$1 Customers Feed")
      .replace(/\bprospects?\s+monitoring\b/gi, "Customers Feed")
      .replace(/\bprospects?\b/gi, "Customers Feed")
      .replace(/\bcustomer\s+feed\b/gi, "Customers Feed")
      .replace(/\bcustomers\s+feed\s+monitoring\b/gi, "Customer Feed monitoring");

    return text;
  };

  const levelMap = {
    Free: 0,
    Starter: 1,
    Pro: 2,
    Elite: 3,
  };

  const icons = [
    ChatIcon,
    LightbulbIcon,
    TuneIcon,
    GroupsIcon,
    EventRepeatIcon,
    SupportIcon,
    SupportAgentIcon,
  ];
  const featuresIcon = [
    ...icons.map((Icon, index) => (
      <Icon key={index} sx={{ color: iconColor }} />
    )),
    <StarIcon key="star" sx={{ color: "#EAAB3E" }} />,
  ];

  const onHandleClick = () => {
    if (isPayingUser) {
      window.open(stripePortalSessionUrl, "_blank");
    } else {
      navigate(
        `/Billing/Checkout?plan=${lowerFirst(plan.Plan)}&type=${currentTab}`
      );
    }

    mixpanelEvent("plan_selected", {
      plan_name: plan?.["Plan"],
      plan_price: `$${plan?.["Monthly Price"]}`,
      plan_duration: currentTab,
      screen_name: "Billing Page",
    });
  };

  const renderPrice = (amount) => {
    if (isFree) return "$ 0.00";
    if (amount === 0 || amount === "0") return "Free";
    return `$ ${Number(amount).toFixed(2)}`;
  };

  const renderFeatures = (text, index) => {
    if (isFree) {
      return (
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          <LockIcon sx={{ color: iconColor }} />
          <Typography sx={{ color: iconColor, textDecoration: "line-through" }}>{text}</Typography>
        </Stack>
      );
    }

    return (
      <Stack direction={"row"} gap={1} alignItems={"center"}>
        {featuresIcon[index]}
        <Typography>{parse(formatFeatureText(text))}</Typography>
      </Stack>
    );
  };

  const renderFooter = () => {
    if (userPlan === currentPlan) {
      if (isFree) {
        return (
          <Box
            color="#000000"
            fontWeight={600}
            sx={{
              background: "linear-gradient(to top left, #f4f4f4, #ffffff)",
            }}
            py={1}
            px={3}
            borderRadius={"8px"}
            mt={2}
            textAlign={"center"}
            width={"fit-content"}
            alignSelf={"center"}
            justifySelf={"center"}
          >
            Current Status
          </Box>
        );
      }
      return (
        <Box
          color="#000000"
          fontWeight={600}
          sx={{
            background: "linear-gradient(to top left, #f4f4f4, #ffffff)",
          }}
          py={1}
          px={3}
          borderRadius={"8px"}
          mt={2}
          textAlign={"center"}
          width={"fit-content"}
          alignSelf={"center"}
          justifySelf={"center"}
        >
          Current Plan
        </Box>
      );
    }
    if (isFree) return null;
    return (
      <Box width={"100%"} pt={2}>
        <Button
          style={{
            width: "100%",
            border: "2px solid #000000",
            color: "#000000",
            fontWeight: 600,
          }}
          sx={{
            "&:hover": {
              backgroundColor: "#f4f4f4",
              borderColor: "#000000",
            },
          }}
          variant="outlined"
          onClick={onHandleClick}
        >
          {levelMap[userPlan] > levelMap[currentPlan] ? "Downgrade" : "Upgrade"}
        </Button>
      </Box>
    );
  };

  return (
    <Stack
      justifyContent={"space-between"}
      gridColumn={"span 1"}
      height="100%"
      px={3}
      py={4}
      backgroundColor="#FFF"
      borderRadius="14px"
      boxShadow="0px 0px 6px 0px rgba(0, 0, 0, 0.02), 0px 2px 4px 0px rgba(0, 0, 0, 0.08)"
      position={"relative"}
    >
      {isPromoted && (
        <Box
          position={"absolute"}
          border="1px solid #EAAB3E"
          borderRadius={"10px"}
          p={0.5}
          px={1.5}
          backgroundColor="#FEFBF5"
          boxShadow={"0px 4px 4px 0px rgba(234, 171, 62, 0.12);"}
          top={-16}
          left={0}
          right={0}
          marginLeft={"auto"}
          marginRight={"auto"}
          color="#EAAB3E"
          width={"fit-content"}
          fontWeight={500}
        >
          👇 Our Best Seller
        </Box>
      )}
      <Stack>
        <Stack pb={2} gap={1}>
          <Typography color="#415058" fontWeight={700} fontSize={"1.15rem"}>
            {isFree && !isTrial ? "Trial Expired" : plan?.Plan}
          </Typography>
          <Typography fontSize={"2rem"} color="#1F292E" fontWeight={700}>
            {renderPrice(priceMap[currentTab])}
            <span
              style={{
                color: "#415058",
                fontWeight: 500,
                fontSize: ".875rem",
              }}
            >
              {" "}
              / month
            </span>
          </Typography>
        </Stack>
        <Divider />
        <Stack py={2} pt={3} gap={3}>
          {(isFree ? expiredFeatures : features).map((feature, index) =>
            renderFeatures(feature, index)
          )}
        </Stack>
      </Stack>
      <Box width={"100%"}>{renderFooter()}</Box>
    </Stack>
  );
};
